<script setup lang="ts">
import type { ColumnDef, RowData } from '@/components/ui/Table/interfaces'

import { useDebounceFn } from '@vueuse/core'

import {
  getRewardOtherUsageDisabledMessage,
  isRewardAvailableForOtherUsage
} from '@/components/rewards/utils/rewardHelpers'

import type { Icons } from '@/components/base/BaseIcon.vue'
import type { LoyaltyReward } from '@/types/types'

const REWARD_TYPE_CHIP_ICONS: Record<string, Icons> = {
  free_product: 'HeroGiftIconSolid',
  voucher_discount_percent: 'HeroReceiptPercentIconSolid',
  voucher_discount_amount: 'HeroReceiptRefundIconSolid',
  voucher_x_for_y: 'HeroShoppingBagIconOutline'
}

export interface RewardSlot {
  name: string
  probability: number
}

interface Table {
  pagination: {
    currentPage: number
    lastPage: number
    total: number
  }
  columns: {
    select?: ColumnDef
    image: ColumnDef
    type: ColumnDef
    name: ColumnDef
    points?: ColumnDef
    slot?: ColumnDef
  }
  rows: RowData[]
}

const { t } = useI18n()
const { formatNumber } = useNumber()

const props = withDefaults(
  defineProps<{
    rewards: LoyaltyReward[]
    isOpen: boolean
    showEditButton?: boolean
    showPoints?: boolean
    translationKey?: string
    allowOfferRewards?: boolean
    multiple?: boolean
    maxSelected?: number
    slots?: RewardSlot[]
    isLoading?: boolean
    preselectedIds?: number[]
  }>(),
  {
    showEditButton: false,
    showPoints: false,
    allowOfferRewards: false,
    multiple: false,
    maxSelected: undefined,
    slots: () => [],
    isLoading: false,
    preselectedIds: () => []
  }
)

const emit = defineEmits<{
  (e: 'closeModal'): void
  (e: 'createReward', selectedIds: number[]): void
  (e: 'selectReward', rewardId: number): void
  (e: 'selectRewards', rewardIds: number[]): void
}>()

const selectedIds = ref<number[]>([])

const isSelected = (rewardId: number) => selectedIds.value.includes(rewardId)

const isMaxReached = computed(() => props.maxSelected !== undefined && selectedIds.value.length >= props.maxSelected)

const toggleSelection = (rewardId: number) => {
  selectedIds.value = isSelected(rewardId)
    ? selectedIds.value.filter((id) => id !== rewardId)
    : [...selectedIds.value, rewardId]
}

const onRowClicked = (rewardId: number) => {
  if (props.multiple) toggleSelection(rewardId)
  else emit('selectReward', rewardId)
}

const confirmLabel = computed(() => {
  const count = selectedIds.value.length
  const limit = props.maxSelected !== undefined ? ` (${count} / ${props.maxSelected})` : ` (${count})`

  return `${t('global.words.add')}${limit}`
})

const slotFor = (rewardId: number): RewardSlot | null => {
  const position = selectedIds.value.indexOf(rewardId)
  if (position === -1) return null

  const slot = props.slots[position]
  if (slot) return slot

  return {
    name: t('pages.autopilot.autopilotDetailPage.scenarioOptions.reward.modal.slotPosition', {
      position: position + 1
    }),
    probability: 100 / selectedIds.value.length
  }
}

watch(
  () => props.isOpen,
  (isOpen) => {
    selectedIds.value = isOpen ? props.preselectedIds.slice(0, props.maxSelected) : []
  }
)

const search = useDebounceFn(async (event?: string) => {
  if (event === '' || !!event) {
    searchText.value = event
  }
}, 5000)

const searchText = ref('')

const modalTitle = computed(() =>
  props.translationKey
    ? t(`${props.translationKey}.title`)
    : t('pages.autopilot.autopilotDetailPage.scenarioOptions.reward.modal.title')
)
const createLabel = computed(() =>
  props.translationKey
    ? t(`${props.translationKey}.create`)
    : t('pages.autopilot.autopilotDetailPage.scenarioOptions.reward.modal.create')
)
const useLabel = computed(() =>
  props.translationKey
    ? t(`${props.translationKey}.use`)
    : t('pages.autopilot.autopilotDetailPage.scenarioOptions.reward.modal.use')
)
const searchPlaceholder = computed(() =>
  props.translationKey
    ? t(`${props.translationKey}.placeholder`)
    : t('pages.autopilot.autopilotDetailPage.scenarioOptions.reward.modal.placeholder')
)

const table = ref<Table>({
  pagination: {
    currentPage: 1,
    lastPage: 1,
    total: 1
  },
  columns: {
    image: {
      headerClass: 'whitespace-nowrap w-1/12!'
    },
    type: {
      label: 'Type',
      headerClass: 'whitespace-nowrap w-1/12!'
    },
    name: {
      label: 'Nom',
      headerClass: 'whitespace-nowrap w-1/2!'
    }
  },
  rows: []
})

const isRewardUnavailable = (reward: LoyaltyReward) =>
  !props.allowOfferRewards && !isRewardAvailableForOtherUsage(reward)

const buildTableRow = (reward: LoyaltyReward): RowData => {
  const isUnavailable = isRewardUnavailable(reward)
  const isLocked = props.multiple && isMaxReached.value && !isSelected(reward.id)

  return {
    id: reward.id,
    disabled: isUnavailable || isLocked,
    disabledTooltip: isUnavailable ? getRewardOtherUsageDisabledMessage(reward, t) : undefined,
    data: {
      image: reward.pictureUrl,
      type: { name: reward.rewardType.name, slug: reward.rewardType.slug },
      name: reward.name,
      points: reward.points
    }
  }
}

const filteredRowsRewards = computed<RowData[]>(() => {
  return props.rewards
    .filter((reward) => reward.name.toLowerCase().includes(searchText.value.toLowerCase()))
    .sort((a, b) => Number(isRewardUnavailable(a)) - Number(isRewardUnavailable(b)))
    .map(buildTableRow)
})

onMounted(() => {
  if (props.multiple) {
    table.value.columns = {
      select: { headerClass: 'w-8!' },
      ...table.value.columns,
      slot: {
        label: t('pages.autopilot.autopilotDetailPage.scenarioOptions.reward.modal.slot'),
        headerClass: 'whitespace-nowrap w-1/4!'
      }
    }
  }

  if (props.showPoints) {
    table.value.columns.points = {
      label: t('global.words.points'),
      headerClass: 'whitespace-nowrap w-1/2!',
      numeric: true
    }
  }
})
</script>

<template>
  <BaseModal
    :is-open="isOpen"
    :heading-title="modalTitle"
    size="6xl"
    :show-footer="multiple"
    :link-text="multiple ? confirmLabel : ''"
    :cancel="multiple ? t('global.words.cancel') : null"
    :is-disabled="!selectedIds.length || isLoading"
    @click="emit('selectRewards', selectedIds)"
    @close="emit('closeModal')"
  >
    <div class="flex flex-col">
      <div class="flex w-full flex-1 flex-col items-start break-words text-left">
        <div>
          <p class="font-medium text-foreground">
            {{ createLabel }}
          </p>
          <Button variant="outline" class="mt-4" @click="emit('createReward', selectedIds)">
            <BaseIcon name="HeroPlusIconOutline" class="h-5 w-5" />
            {{ createLabel }}
          </Button>
        </div>
        <div class="mt-4 w-full">
          <div class="mb-4 flex w-full flex-wrap items-center justify-between">
            <p class="font-medium text-foreground">
              {{ useLabel }}
            </p>
            <div class="relative w-full sm:w-max">
              <Input
                v-model="searchText"
                type="search"
                class="pl-10 sm:w-96"
                :placeholder="searchPlaceholder"
                @input="search(($event.target as HTMLInputElement).value)"
              />
              <BaseIcon
                name="HeroMagnifyingGlassIconOutline"
                class="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 transform text-foreground"
              />
            </div>
          </div>

          <AppTable
            v-if="isLoading || filteredRowsRewards.length > 0"
            :columns="table.columns"
            :rows="filteredRowsRewards"
            :is-loading="isLoading"
            :loader-rows-number="5"
            row-classes="cursor-pointer"
            @row-clicked="(row) => onRowClicked(row.id as number)"
          >
            <template #td-select="{ row }">
              <Checkbox
                :model-value="isSelected(row.id as number)"
                :disabled="row.disabled"
                class="pointer-events-none"
              />
            </template>

            <template #td-image="{ data }">
              <img :src="data.image" class="h-10 w-16 min-w-16 rounded-md object-cover object-center" />
            </template>

            <template #td-type="{ data }">
              <div class="text-sm flex w-full flex-row justify-start">
                <BaseChip :icon="REWARD_TYPE_CHIP_ICONS[data.type.slug]" :text="data.type.name" />
              </div>
            </template>

            <template #td-name="{ data }">
              <div class="flex items-center gap-2 text-sm w-full text-left text-foreground">
                {{ data.name }}
              </div>
            </template>

            <template #td-slot="{ row }">
              <div v-if="slotFor(row.id as number)" class="flex flex-col text-sm">
                <span class="font-medium text-foreground">{{ slotFor(row.id as number).name }}</span>
                <span class="text-xs text-muted-foreground">
                  {{ formatNumber(slotFor(row.id as number).probability) }} %
                </span>
              </div>
              <span v-else class="text-sm text-muted-foreground">-</span>
            </template>
          </AppTable>
        </div>
      </div>
    </div>
  </BaseModal>
</template>
